import { ChangeDetectionStrategy, Component, HostBinding, inject, OnChanges } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { map, of, switchMap } from 'rxjs';

import { NavigationElementType } from '@blaze-runtime/engine-domain';
import { fromRouter } from '@blaze-shared/app-state-data-access';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { getHostSlotIdOfNavigationElement } from '../../canvas-elements/element-model/slots';
import { LayoutLoadingService } from '../../services/layout-loading.service';

import { CanvasElementBaseComponent } from './base.component';

@UntilDestroy()
@Component({
  template: '',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class CanvasElementBaseNavigationComponent<T extends NavigationElementType>
  extends CanvasElementBaseComponent<T>
  implements CanvasElementComponent<T>, OnChanges
{
  private readonly layoutLoading = inject(LayoutLoadingService);

  @HostBinding('class.be-hidden-navigation') isHiddenPublished = true;

  constructor() {
    super();

    /**
     * Hide navigation elements in published mode to prevent content jumping if visibility is set
     */
    this.store
      .select(fromRouter.getIsInEditor)
      .pipe(
        switchMap((isInEditor) => {
          if (isInEditor) {
            return of(true);
          }

          return this.element$.pipe(
            map((element) => {
              const hostSlotId = getHostSlotIdOfNavigationElement(element);
              if (!hostSlotId) {
                return undefined;
              }

              const hostSlot = element.slots[hostSlotId];
              return hostSlot?.hosting?.ns;
            }),
            switchMap((layoutId) => {
              if (!layoutId) {
                return of(true);
              }

              return this.layoutLoading.loadedLayoutIds$.pipe(
                map((layoutIds) => layoutIds.includes(layoutId)),
              );
            }),
          );
        }),
        untilDestroyed(this),
      )
      .subscribe((isVisible) => {
        this.isHiddenPublished = !isVisible;
        this.cdr.markForCheck();
      });
  }

  ngOnChanges(): void {
    // Required for visibility to work
  }
}
